import { useState } from 'react';
import { Link } from 'react-router-dom';
import { allPages, search } from '../lib/api';
import { useAsync } from '../lib/useAsync';
import { Chip } from '../components/Chips';
import type { PageSummary, SearchResult } from '../lib/types';

const TYPE_ORDER = ['system', 'issue', 'runbook', 'investigation', 'decision', 'rule', 'work'];

export default function SearchPage() {
  const [q, setQ] = useState('');
  const [submitted, setSubmitted] = useState('');
  const [results, setResults] = useState<SearchResult[] | null>(null);
  const [semantic, setSemantic] = useState(false);
  const [searching, setSearching] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const pages = useAsync<PageSummary[]>(allPages, []);

  async function run(e: React.FormEvent) {
    e.preventDefault();
    const term = q.trim();
    if (!term) return;
    setSearching(true);
    setError(null);
    setSubmitted(term);
    try {
      const r = await search(term);
      setResults(r.results);
      setSemantic(r.semantic);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSearching(false);
    }
  }

  return (
    <>
      <h1>Knowledge</h1>
      <form className="search-form" onSubmit={run}>
        <input
          type="search"
          placeholder="Describe the problem in your own words"
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
        <button disabled={searching || !q.trim()}>Search</button>
      </form>
      <p className="hint">
        {semantic
          ? 'Ranked by meaning and by words, so a symptom described differently still finds its page.'
          : 'Ranked by words. Semantic ranking needs GEMINI_API_KEY on the deployment.'}
      </p>

      {error ? <div className="error">{error}</div> : null}

      {results ? (
        <>
          <h2>
            {results.length} result{results.length === 1 ? '' : 's'} for {submitted}
          </h2>
          {results.map((r) => (
            <Link className="result" key={r.path} to={`/kb/${r.path}`}>
              <div className="rtitle">{r.title}</div>
              <div className="rpath">
                {r.path}
                {r.vec_rank && !r.ft_rank ? '  (found by meaning)' : ''}
              </div>
              {r.description ? <div className="rdesc">{r.description}</div> : null}
            </Link>
          ))}
        </>
      ) : null}

      {!results && pages.data ? (
        <>
          {TYPE_ORDER.map((type) => {
            const list = pages.data!.filter((p) => p.type === type);
            if (!list.length) return null;
            return (
              <section key={type}>
                <div className="group-head">
                  <h2>{type}s</h2>
                  <span className="count">{list.length}</span>
                </div>
                {list.map((p) => (
                  <Link className="result" key={p.path} to={`/kb/${p.path}`}>
                    <div className="rtitle">
                      {p.title}{' '}
                      {p.confidence && p.confidence !== 'verified' ? <Chip>{p.confidence}</Chip> : null}
                    </div>
                    {p.description ? <div className="rdesc">{p.description}</div> : null}
                  </Link>
                ))}
              </section>
            );
          })}
        </>
      ) : null}

      {!results && pages.loading ? <p className="loading">Loading pages...</p> : null}
      {pages.error ? <div className="error">{pages.error}</div> : null}
    </>
  );
}
